import { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Text,
  Flex,
  Button,
  Image,
  Icon,
  Textarea,
  CircularProgress,
  useToast,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { IoIosArrowBack } from "react-icons/io";
import { FaRegCircleCheck } from "react-icons/fa6";
import { IoWarningOutline } from "react-icons/io5";

import {
  getBookingStatus,
  getBookingDetailsByService,
  raiseDispute,
} from "../services/booking";
import { getCookie, formatDurationHM, extractDatePart } from "../core/utils";
import { SERVICES_IMAGE_URL } from "../core/constants";
import { OpenInAppBanner } from "../components/OpenInAppBanner";
import LogoIcon from "../assets/images/logoIcon.png";
import LogoText from "../assets/images/logoText.png";

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
const isAndroid = /Android/.test(navigator.userAgent);
const isMobile = isIOS || isAndroid;

export const RaiseDispute = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const jwt = getCookie("book-d booking token");

  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [disputeRaised, setDisputeRaised] = useState(false);
  const [message, setMessage] = useState("");
  const [booking, setBooking] = useState<any>(null);

  // iOS Smart App Banner — shows a native prompt in Safari, no JS redirect
  useEffect(() => {
    if (!isIOS || !id) return;
    const meta = document.createElement("meta");
    meta.name = "apple-itunes-app";
    meta.content = `app-id=6558535790, app-argument=bookd://raise-dispute/${id}`;
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, [id]);

  useEffect(() => {
    if (!jwt) {
      navigate("/login", {
        state: { returnTo: `/raise-dispute/${id}` },
        replace: true,
      });
    }
  }, [jwt, id, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!id || !jwt) return;

      try {
        const [statusRes, detailsRes] = await Promise.all([
          getBookingStatus(id),
          getBookingDetailsByService(id),
        ]);

        if (
          statusRes?.status === 200 &&
          statusRes?.data?.disputeRaisedByCustomer === true
        ) {
          setDisputeRaised(true);
        }

        if (detailsRes?.status === 200 && detailsRes?.data?.length > 0) {
          setBooking(detailsRes.data[0]);
        }
      } catch {
        // silently fail
      }

      setFetchingData(false);
    };

    fetchData();
  }, [id, jwt]);

  const handleSubmit = async () => {
    if (!id || !message.trim()) {
      toast({
        title: "Message required",
        description: "Please describe your dispute before submitting.",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    setLoading(true);

    try {
      const res = await raiseDispute(id, message.trim());

      if (res?.status === 200) {
        setDisputeRaised(true);
        toast({
          title: "Dispute Submitted",
          description: res.message || "Your dispute has been submitted.",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      } else {
        toast({
          title: "An error occurred",
          description:
            res?.message || "Unable to submit dispute. Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      }
    } catch {
      toast({
        title: "An error occurred",
        description: "Something went wrong. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    }

    setLoading(false);
  };

  if (!jwt) return null;

  return (
    <Box
      display="flex"
      flexDir="column"
      minH="100vh"
      bg="linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 40%, #FFF7F3 70%, #D8F1F2 100%)"
    >
      <Flex
        boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
        minH="72px"
        px={{ base: "20px", md: "10%" }}
        align="center"
        justify="space-between"
        bg="white"
      >
        <Flex align="center" gap="16px">
          <Icon
            fontSize={24}
            color="#19202D"
            cursor="pointer"
            onClick={() => navigate("/")}
          >
            <IoIosArrowBack />
          </Icon>

          <Image
            src={LogoIcon}
            alt="Book-d Logo"
            h="32px"
            w="19px"
            cursor="pointer"
            onClick={() => navigate("/")}
          />
        </Flex>
      </Flex>

      {isMobile && (
        <OpenInAppBanner
          deepLink={`bookd://raise-dispute/${id}`}
          isIOS={isIOS}
        />
      )}

      <Flex flex={1} align="flex-start" justify="center" px="20px" py="40px">
        <Flex
          flexDir="column"
          align="center"
          maxW="580px"
          w="100%"
          bg="white"
          borderRadius="24px"
          boxShadow="0px 4px 20px rgba(0, 0, 0, 0.08)"
          p={{ base: "32px 24px", md: "48px 40px" }}
        >
          {fetchingData ? (
            <Flex py="40px">
              <CircularProgress isIndeterminate color="#FF8E3C" size="60px" />
            </Flex>
          ) : disputeRaised ? (
            <>
              <Flex
                w="80px"
                h="80px"
                borderRadius="full"
                bg="#DBF1F2"
                align="center"
                justify="center"
                mb="24px"
              >
                <FaRegCircleCheck size={44} color="#267684" />
              </Flex>

              <Heading
                fontSize={{ base: "24px", md: "28px" }}
                color="#19202D"
                fontFamily="RedHatDisplay-Bold"
                textAlign="center"
              >
                Dispute Submitted
              </Heading>

              <Box
                h="3px"
                w="60px"
                borderRadius="10px"
                bg="#FF8E3C"
                my="20px"
              />

              <Text
                fontSize={{ base: "15px", md: "16px" }}
                color="#4F4F51"
                lineHeight="26px"
                textAlign="center"
                maxW="400px"
              >
                Your dispute has been previously submitted. Our team will review
                it and contact you soon once it's resolved or if any additional
                information is needed.
              </Text>

              <Button
                mt="32px"
                bg="#FF8E3C"
                _hover={{ bg: "#e07a30" }}
                borderRadius="30px"
                color="white"
                h="52px"
                px="40px"
                fontSize="16px"
                fontFamily="RedHatDisplay-Bold"
                onClick={() => navigate("/")}
              >
                Back to Home
              </Button>
            </>
          ) : (
            <>
              <Flex
                w="80px"
                h="80px"
                borderRadius="full"
                bg="#FFF0E5"
                align="center"
                justify="center"
                mb="24px"
              >
                <IoWarningOutline size={40} color="#FF8E3C" />
              </Flex>

              <Heading
                fontSize={{ base: "24px", md: "28px" }}
                color="#19202D"
                fontFamily="RedHatDisplay-Bold"
                textAlign="center"
              >
                Raise a Dispute
              </Heading>

              <Box
                h="3px"
                w="60px"
                borderRadius="10px"
                bg="#FF8E3C"
                my="20px"
              />

              {booking && (
                <Box
                  w="100%"
                  borderRadius="16px"
                  border="1px solid #E9EDEF"
                  p={{ base: "16px", md: "20px" }}
                  mb="24px"
                >
                  <Heading
                    fontSize="14px"
                    color="#9BA3A3"
                    fontFamily="RedHatDisplay-Bold"
                    mb="16px"
                    textTransform="uppercase"
                    letterSpacing="0.5px"
                  >
                    Booking Summary
                  </Heading>

                  <Flex gap="16px" align="flex-start">
                    <Image
                      src={`${SERVICES_IMAGE_URL}/${booking.services?.serviceImage}`}
                      alt={booking.services?.serviceName}
                      w="80px"
                      h="80px"
                      borderRadius="12px"
                      objectFit="cover"
                      flexShrink={0}
                    />

                    <Box flex={1}>
                      <Heading
                        fontSize="16px"
                        color="#19202D"
                        fontFamily="RedHatDisplay-Bold"
                        mb="8px"
                      >
                        {booking.services?.serviceName}
                      </Heading>

                      <Flex flexWrap="wrap" gap="6px" mb="12px">
                        <Box bg="#DBF1F2" borderRadius="4px" px="8px" py="3px">
                          <Text
                            fontSize="13px"
                            fontWeight={600}
                            color="#267684"
                          >
                            {extractDatePart(booking.slot_start_time, "date")}
                          </Text>
                        </Box>

                        <Box bg="#DBF1F2" borderRadius="4px" px="8px" py="3px">
                          <Text
                            fontSize="13px"
                            fontWeight={600}
                            color="#267684"
                          >
                            {extractDatePart(booking.slot_start_time, "time")}
                          </Text>
                        </Box>

                        <Box bg="#DBF1F2" borderRadius="4px" px="8px" py="3px">
                          <Text
                            fontSize="13px"
                            fontWeight={600}
                            color="#267684"
                          >
                            {formatDurationHM(booking.serviceDuration)}
                          </Text>
                        </Box>
                      </Flex>
                    </Box>
                  </Flex>

                  <Box h="1px" w="100%" bg="#E9EDEF" my="12px" />

                  <Flex justify="space-between" gap="12px" flexWrap="wrap">
                    <Box>
                      <Text fontSize="12px" color="#9BA3A3" fontWeight={600}>
                        Amount
                      </Text>
                      <Text fontSize="16px" color="#19202D" fontWeight={700}>
                        £{booking.servicePrice}
                      </Text>
                    </Box>

                    <Box>
                      <Text fontSize="12px" color="#9BA3A3" fontWeight={600}>
                        Discount
                      </Text>
                      <Text fontSize="16px" color="#19202D" fontWeight={700}>
                        £{booking.discount_amount}
                      </Text>
                    </Box>

                    <Box>
                      <Text fontSize="12px" color="#9BA3A3" fontWeight={600}>
                        Paid
                      </Text>
                      <Text fontSize="16px" color="#267684" fontWeight={700}>
                        £{booking.paid_amount}
                      </Text>
                    </Box>
                  </Flex>
                </Box>
              )}

              <Box w="100%">
                <Heading
                  fontSize="16px"
                  color="#19202D"
                  mb="8px"
                  fontFamily="RedHatDisplay-Bold"
                >
                  Describe your dispute
                </Heading>

                <Textarea
                  placeholder="Please explain in detail what happened and why you are raising this dispute..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  borderRadius="16px"
                  border="1px solid #E9EDEF"
                  fontSize="15px"
                  color="#19202D"
                  _placeholder={{ color: "#9BA3A3" }}
                  _focusVisible={{ borderColor: "#FF8E3C" }}
                  minH="120px"
                  resize="vertical"
                  p="16px"
                />
              </Box>

              <Button
                mt="24px"
                bg="#FF8E3C"
                _hover={{ bg: "#e07a30" }}
                borderRadius="30px"
                color="white"
                h="58px"
                w={{ base: "100%", md: "280px" }}
                fontSize="17px"
                fontFamily="RedHatDisplay-Bold"
                onClick={handleSubmit}
                isLoading={loading}
                loadingText="Submitting..."
                disabled={loading}
              >
                Submit Dispute
              </Button>
            </>
          )}

          {!fetchingData && (
            <Flex align="center" gap="6px" mt="40px" opacity={0.5}>
              <Text fontSize="13px" color="#4F4F51">
                Powered by
              </Text>
              <Image src={LogoText} h="18px" objectFit="contain" />
            </Flex>
          )}
        </Flex>
      </Flex>
    </Box>
  );
};
